'use client';

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import apiClient from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import {
  readCachedTenants,
  readTenantSlug,
  writeCachedTenants,
  writeTenantSlug,
} from '@/lib/tenantStorage';
import { useMe } from '@/hooks/useMe';

const PUBLIC_PATHS = ['/login', '/sign-in'];
const AUTH_DISABLED = process.env.NEXT_PUBLIC_AUTH_DISABLED === 'true';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  isGroup?: boolean;
  // Entity (tenant group) info. On real tenants: which entity they belong
  // to (from /tenants). On group pseudo-tenants: the entity the aggregate
  // view targets (sent to /group/* as ?group=).
  group_slug?: string | null;
  group_name?: string | null;
}

export const GROUP_SLUG = '__group__';
export const DEFAULT_GROUP = 'alkemy-plus';

// Pseudo-tenant slug for an entity's aggregate view. Alkemy+ keeps the
// legacy bare '__group__' (stored slugs / bookmarks keep working); other
// entities get a suffixed variant. The pseudo slug itself never appears in
// URLs — group pages live under /group/<entity>/* with the plain entity
// slug as the path segment.
export function groupPseudoSlug(groupSlug: string): string {
  return groupSlug === DEFAULT_GROUP ? GROUP_SLUG : `${GROUP_SLUG}:${groupSlug}`;
}

// Pick a tenant from the user's email domain — slug substring first
// (e.g. luca.lai@witailer.retex.com → "witailer"), then first word of the
// tenant name (e.g. mario@alkemy.com → "alkemy" → Alkemy Media).
function tenantFromEmail(email: string | undefined, tenants: Tenant[]): Tenant | undefined {
  if (!email) return undefined;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return undefined;
  return (
    tenants.find((t) => t.slug && domain.includes(t.slug)) ||
    tenants.find((t) => {
      const firstWord = t.name.toLowerCase().split(/\s+/)[0];
      return firstWord.length >= 3 && domain.includes(firstWord);
    })
  );
}

function currentUserEmail(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.__msalInstance?.getAllAccounts?.()?.[0]?.username;
}

interface TenantContextValue {
  tenants: Tenant[];
  realTenants: Tenant[];
  // Display-only: the live `tenants` once loaded, else the last list this
  // browser saw (localStorage). Lets the header switcher and breadcrumb keep
  // their shape while /tenants/ is in flight. Never use it to validate a
  // slug or gate a query — that's `tenants` / `currentTenant` (whose one
  // optimistic use of the cached list is explained where it's computed).
  knownTenants: Tenant[];
  currentTenant: Tenant | null;
  // Stable slug for use in React Query keys. '' while tenants are loading
  // so queries can gate on it with `enabled: !!tenantSlug` and avoid firing
  // a request before we know which tenant context to send.
  tenantSlug: string;
  isGroup: boolean;
  // Entity slug of the active aggregate view ('alkemy-plus',
  // 'alkemy-consulting', …) when isGroup, else null. Group hooks send it
  // to /group/* as ?group=.
  groupSlug: string | null;
  switchTenant: (slug: string) => void;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextValue>({
  tenants: [],
  realTenants: [],
  knownTenants: [],
  currentTenant: null,
  tenantSlug: '',
  isGroup: false,
  groupSlug: null,
  switchTenant: () => {},
  isLoading: true,
});

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isPublic = PUBLIC_PATHS.some((p) => pathname?.startsWith(p));
  const [currentSlug, setCurrentSlug] = useState('');

  useEffect(() => {
    const saved = readTenantSlug();
    if (saved) setCurrentSlug(saved);
  }, []);

  // Skip the API call on public auth pages — there's no token yet, every
  // request 401s, and the retries (4×) flood the console.
  const tenantsQuery = useQuery<Tenant[]>({
    queryKey: queryKeys.tenants(),
    queryFn: () => apiClient.get('/api/v1/tenants/').then((r) => r.data),
    staleTime: Infinity,
    // Local dev (AUTH_DISABLED): the API serves /tenants/ without a token,
    // so the query must run — otherwise no tenant ever resolves and the app
    // hangs on the root spinner. Public auth pages stay skipped (no token,
    // every request 401s and the retries flood the console).
    enabled: AUTH_DISABLED || !isPublic,
  });

  const realTenants: Tenant[] = tenantsQuery.data ?? [];
  const me = useMe();
  const groupAccess = me.data?.group_access === true;

  // Group pseudo-tenants appear only when the user has the explicit
  // ``group_access`` flag: one aggregate entry per entity the user belongs
  // to (me.groups, membership-derived). Mirrors the server gate, which
  // requires flag + membership for EVERY entity — no legacy flag-only
  // Alkemy+ entry, it would just 403. The previous heuristic — "show
  // whenever the user can see ≥ 2 tenants" — leaked the aggregated view to
  // any cross-tenant admin and gave no way to *deny* it.
  const meGroups = me.data?.groups;
  const tenants: Tenant[] = useMemo(() => {
    if (!groupAccess || realTenants.length < 1) return realTenants;
    const pseudos: Tenant[] = (meGroups ?? []).map((g) => ({
      id: groupPseudoSlug(g.slug),
      name: g.name,
      slug: groupPseudoSlug(g.slug),
      isGroup: true,
      group_slug: g.slug,
      group_name: g.name,
    }));
    return [...pseudos, ...realTenants];
  }, [realTenants, groupAccess, meGroups]);

  // Last-seen switcher list, hydrated after mount (reading storage during
  // render would mismatch the server HTML) and refreshed from every live
  // list so the next cold load starts from it.
  const [cachedTenants, setCachedTenants] = useState<Tenant[]>([]);
  useEffect(() => {
    setCachedTenants(readCachedTenants());
  }, []);
  useEffect(() => {
    // Only persist once /me settled too, so the cached list includes the
    // group pseudo-tenants the user actually has.
    if (tenantsQuery.data && !me.isLoading) writeCachedTenants(tenants);
  }, [tenants, tenantsQuery.data, me.isLoading]);
  const knownTenants = tenantsQuery.data ? tenants : cachedTenants;

  // Never substitute a different tenant while the stored slug is resolving:
  // the axios interceptor sends the STORED slug header, so falling back to
  // realTenants[0] here made query keys and request headers diverge and
  // polluted the cache with cross-tenant data. While unresolved we expose
  // null/'' and consumers gate on `enabled: !!tenantSlug`.
  const liveTenant = useMemo(
    () => tenants.find((t: Tenant) => t.slug === currentSlug) ?? null,
    [tenants, currentSlug],
  );
  // Optimistic start: while /tenants/ is in flight, a real tenant slug that
  // the URL, the stored pick (= the X-Tenant-Slug the interceptor sends) and
  // the last validated list all agree on is used right away, so the page's
  // data queries run in parallel with /tenants/ + /me instead of after them.
  // Keys and headers can't diverge (URL == stored), and it's no access
  // decision: the API still checks membership, and a revoked slug answers
  // 403 (never 401, which would bounce to /login) until the live list lands
  // and the fallback below picks a valid tenant.
  const optimisticTenant = useMemo(() => {
    if (tenantsQuery.data || isPublic) return null;
    const urlSlug = pathname?.split('/')[1] ?? '';
    if (!urlSlug || urlSlug.startsWith(GROUP_SLUG) || readTenantSlug() !== urlSlug) return null;
    return cachedTenants.find((t) => t.slug === urlSlug && !t.isGroup) ?? null;
  }, [tenantsQuery.data, isPublic, pathname, cachedTenants]);
  const currentTenant = liveTenant ?? optimisticTenant;
  const isGroup = currentTenant?.isGroup === true;

  // Auto-set slug when tenants load. If the saved slug is invalid (user no
  // longer a member, or fresh session), match the email domain to a tenant.
  // Falls back to the first tenant alphabetically.
  //
  // Defense-in-depth: re-read storage here too. If the hydration effect above
  // hasn't flushed yet (e.g. both effects run in the same commit on a fresh
  // mount), `currentSlug` is still '' even though storage holds the user's
  // real pick. Without this check we'd fall through to the email-derived
  // default and overwrite storage with it.
  useEffect(() => {
    if (realTenants.length === 0) return;
    // URL wins over everything: when the user lands on /<slug>/… the page
    // must show THAT tenant's data from the first render. Without this, a
    // stale localStorage slug (e.g. last session's tenant) resolved first
    // and the dashboard briefly rendered another BU's numbers under the
    // URL's tenant — useTenantSlugSync corrected it only a beat later.
    // Group pages carry their entity in the URL too (/group/<entity>/…),
    // and it must win the same way: previously the group routes had no
    // slug segment, so during the switch-to-entity navigation this effect
    // saw the OLD real-tenant pathname, resurrected that slug and the
    // overview page bounced the user back to the default entity.
    const segments = pathname?.split('/') ?? [];
    const urlSlug =
      segments[1] === 'group'
        ? segments[2] && segments[2] !== 'overview' && segments[2] !== 'upload'
          ? groupPseudoSlug(segments[2])
          : ''
        : (segments[1] ?? '');
    const stored = readTenantSlug() ?? '';
    const candidate =
      (urlSlug.startsWith(GROUP_SLUG) || realTenants.some((t) => t.slug === urlSlug)
        ? urlSlug
        : '') ||
      currentSlug ||
      stored;
    // The group pseudo-tenant is valid only with explicit group_access. While
    // /me is still loading, keep the pick (and the null currentTenant loading
    // state) instead of discarding it; once access is denied, fall through to
    // the explicit email/first-tenant fallback below so storage is rewritten
    // with a real slug — never silently substituted at read time.
    const candidateIsValid = candidate.startsWith(GROUP_SLUG)
      ? me.isLoading || tenants.some((t) => t.slug === candidate)
      : realTenants.some((t) => t.slug === candidate);
    if (candidateIsValid) {
      if (currentSlug !== candidate) {
        setCurrentSlug(candidate);
        writeTenantSlug(candidate);
      }
      return;
    }
    const matched = tenantFromEmail(currentUserEmail(), realTenants);
    const slug = matched?.slug ?? realTenants[0].slug;
    setCurrentSlug(slug);
    writeTenantSlug(slug);
  }, [realTenants, tenants, currentSlug, pathname, groupAccess, me.isLoading]);

  const switchTenant = useCallback(
    (slug: string) => {
      setCurrentSlug(slug);
      writeTenantSlug(slug);
      // No cache nuke. Every tenant-scoped query key now embeds the tenant
      // slug (see queryKeys.ts), so changing the slug naturally produces a
      // new cache entry per tenant: React Query fires a fresh fetch and
      // the old tenant's data sits idle until gcTime expires. The previous
      // queryClient.clear() worked, but it also wiped the tenants list
      // query, causing the switcher to flicker to null mid-switch and the
      // alphabetical fallback (Witailer) to snap in before refetch landed.
    },
    [],
  );

  const tenantSlug = currentTenant?.slug ?? '';
  const groupSlug = isGroup ? (currentTenant?.group_slug ?? DEFAULT_GROUP) : null;
  // Stay in the loading state while the active tenant is still resolving
  // (tenants loaded but no validated slug yet) so nothing renders against a
  // tenant the user didn't pick.
  const isLoading =
    tenantsQuery.isLoading || (realTenants.length > 0 && !currentTenant);

  // Memoize so the whole tenant-consuming subtree doesn't re-render on every
  // ancestor render — this context sits near the root.
  const value = useMemo(
    () => ({
      tenants,
      realTenants,
      knownTenants,
      currentTenant,
      tenantSlug,
      isGroup,
      groupSlug,
      switchTenant,
      isLoading,
    }),
    [
      tenants,
      realTenants,
      knownTenants,
      currentTenant,
      tenantSlug,
      isGroup,
      groupSlug,
      switchTenant,
      isLoading,
    ],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return useContext(TenantContext);
}
